import { IconButton, CircularProgress } from "@mui/material";
import { Key as KeyIcon, MoreHoriz } from "@mui/icons-material";
import Table from "@components/admin/Table";
import PageNavigator from "@components/admin/PageNavigator";
import SearchBar from "@components/admin/SearchBar";
import apiClient from "@apiClient";
import AdminLayout from "./AdminLayout";
import useSetActiveLink from "@hooks/useSetActiveLink";
import useTable from "@hooks/useTable";
import MyAvatar from "@components/admin/MyAvatar";
import DateFilter from "@components/admin/DateFilter";
import clsx from "clsx";
import PopperWrapper from "@components/admin/PopperWrapper";
import PopperList from "@components/admin/PopperList";
import SideOptions from "@components/admin/SideOptions";
import useAdminLoggedIn from "@hooks/useAdminLoggedIn";
import { useSelector } from "react-redux";

import UsersIcon from "@images/users.svg";
import useDateFilters from "@hooks/useDateFilters";
import useActionFilters from "@hooks/useActionFilters";
import { createDateColumn, getSelectedItems } from "@helpers";
import GroupModeratorAccessForm from "./GroupModeratorAccessForm";
import GroupDetails from "./GroupDetails";
import React, { useState } from "react";

function Groups() {
  useAdminLoggedIn();
  useSetActiveLink(3);

  // ---------------------------
  // ROLE PRIORITY LOGIC (FIXED)
  // ---------------------------
  const { userRecord } = useSelector((state) => state.user);
  const userRole = userRecord?.role || userRecord?.roles || "";

  const roleArray = Array.isArray(userRole)
    ? userRole.map((r) => r.trim().toUpperCase())
    : [userRole.toString().trim().toUpperCase()];

  const isSuperAdmin = roleArray.some((r) => r === "ADMIN");

  // Route-level community admin detection
  const isCommunityAdminRoute =
    window.location.pathname?.startsWith("/community-admin");

  // Use community-admin APIs ONLY if NOT admin
  const useCommunityAdminApi = !isSuperAdmin && isCommunityAdminRoute;
  const [selectedGroup, setSelectedGroup] = useState(null);

  // ---------------------------
  // TABLE DATA HOOK
  // ---------------------------
  const {
    setReload,
    setPage,
    page,
    totalPages,
    count,
    query,
    setQuery,
    data,
    checked,
    setChecked,
    setLimit,
    limit,
    setSortColumns,
    sortColumns,
    loading,
    setDateFilters,
    setFilters,
  } = useTable({
    getData: (defaultParams) =>
      (useCommunityAdminApi
        ? apiClient.getCommunityAdminGroups
        : apiClient.getGroups)(defaultParams),
    pageSize: 10,
  });

  // ---------------------------
  // MODALS & ACTIONS
  // ---------------------------
  const {
    activeFilter,
    getActions,
    getActionLinks,
    getActionButtons,
    getConfirmationModals,
    setActiveFilter,
  } = useActionFilters({
    apiDelete: apiClient.deleteGroups,
    apiDisable: apiClient.disableGroups,
    apiReinstate: apiClient.reinstateGroups,
    setFilters,
    onLinkClick: () => setPage(1),
    getSelectedItems: () => getSelectedItems(checked),
    setReload,
    customActions: [
      {
        label: "View Details",
        generateClickHandler: (item) => {
          return () => {
            setSelectedGroup(item.id);
          };
        },
      },
    ],
  });

  const { dateFilters, activeDateFilter } = useDateFilters({
    setDateFilters,
    prefix: "Created",
    activeFilter: activeFilter,
  });

  // ---------------------------
  // TABLE COLUMNS
  // ---------------------------
  const fields = [
    {
      columnName: "Name",
      field: "name",
      render: (item) => (
        <MyAvatar src={item.icon?.currBucketUrl} name={item.name} />
      ),
    },
    {
      columnName: "Admin",
      render: (item) => item.admin?.firstName + " " + item.admin?.lastName,
    },
    {
      columnName: "Admin Username",
      render: (item) => item.admin?.userName,
    },
    {
      columnName: "Community",
      render: (item) => item.community?.name,
    },
    {
      columnName: "Members",
      render: (item) => item.members?.length,
    },
    createDateColumn(activeFilter),
    {
      columnName: "Actions",
      render: (item) => (
        <PopperWrapper id={item.id}>
          <IconButton>
            <MoreHoriz />
          </IconButton>
          <PopperList items={getActions(item)} />
        </PopperWrapper>
      ),
    },
  ];

  // ---------------------------
  // SIDE OPTIONS LINKS
  // ---------------------------
  const links = [...getActionLinks()];

  // Only super admin (not community admin) can access Moderator Access Levels
  if (isSuperAdmin) {
    links.push({
      text: "Moderator Access Levels",
      icon: <KeyIcon color="primary" alt="Group moderator access levels" />,
      onClick: () => {
        setSelectedGroup(null);
        setActiveFilter("Moderator Access Levels");
      },
    });
  }

  // ---------------------------
  // LOADING STATE
  // ---------------------------
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  // ---------------------------
  // JSX RETURN
  // ---------------------------
  return (
    <AdminLayout>
      <div className="h-full">
        <div className="flex h-full">
          <SideOptions
            headerText={"Groups"}
            headerIcon={<img src={UsersIcon} alt="users" />}
            links={links}
            activeFilter={activeFilter}
          />

          {/* Show GroupDetails when a group is selected */}
          {selectedGroup && (
            <GroupDetails groupId={selectedGroup} setId={setSelectedGroup} />
          )}

          {/* Show Moderator Access Levels form */}
          {!selectedGroup &&
            activeFilter === "Moderator Access Levels" &&
            isSuperAdmin && <GroupModeratorAccessForm />}

          {/* Show main groups table */}
          {!selectedGroup && activeFilter !== "Moderator Access Levels" && (
            <div className="grow px-12 pt-6 overflow-x-auto">
              <div className="flex items-center gap-2">
                <DateFilter
                  count={count}
                  dateFilters={dateFilters}
                  activeDateFilter={activeDateFilter}
                />
                <SearchBar setQuery={setQuery} query={query} />
                <div
                  className={clsx(
                    Object.values(checked).filter((v) => v).length === 0 &&
                      "hidden",
                    "ml-auto flex items-center gap-2"
                  )}
                >
                  {getActionButtons()}
                </div>
              </div>

              <Table
                data={data}
                fields={fields}
                checked={checked}
                setChecked={setChecked}
                setSortColumns={setSortColumns}
                sortColumns={sortColumns}
              />

              {data.length > 0 && (
                <PageNavigator
                  totalPages={totalPages}
                  setPage={setPage}
                  page={page}
                  setLimit={setLimit}
                  limit={limit}
                />
              )}
            </div>
          )}
        </div>

        {getConfirmationModals()}
      </div>
    </AdminLayout>
  );
}

export default Groups;
